import React from 'react';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import {
  useGetFaxToDos,
} from 'pages/Dashboard/pages/Fax/hooks';
import Tag from 'core/components/Tag';
import Spinner from 'pages/Dashboard/components/Spinner';
import UrgentIcon from 'core/icons/urgent';
import {
  ToDo,
} from 'types/toDo';
import Name from 'pages/Dashboard/components/Name';
import {
  useSidePanel,
} from 'core/hooks';
import {
  statusColorClasses,
} from 'pages/Dashboard/pages/Todos/components/StatusSelect';
import useSignalREffect from 'views/EMR/hooks/signalR';

type Props = {
  faxId: number;
};

export function ToDoTooltip({
  name,
  isUrgent,
  category,
  assignee,
  creator,
  currentStatus,
}: ToDo) {
  const users = [
    { key: 'assignee', user: assignee },
    { key: 'creator', user: creator },
  ];
  return (
    <div className="flex w-full flex-col gap-2 overflow-hidden">
      <div className="flex items-center gap-1 font-semibold text-sm">
        {isUrgent && (<UrgentIcon className="!w-4 !h-4 !text-red-500" />)}
        {name}
      </div>
      <div className="flex gap-1 items-center">
        <Tag className={statusColorClasses[currentStatus?.transitionName ?? '']}>
          {currentStatus.statusDisplayName}
        </Tag>
        {category}
      </div>
      <div className="flex w-full gap-2 justify-between items-center">
        {users.map(({ key, user }) => (
          <div
            key={key}
            className="flex flex-col gap-1"
          >
            <span className="text-gray-500 font-semibold">{startCase(key)}</span>
            <Name
              {...user?.formattedNames}
              image={{ size: 'xs' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FaxToDos({ faxId }: Props) {
  const { data = [], isLoading, refetch } = useGetFaxToDos(faxId);
  const { openPanel } = useSidePanel();

  useSignalREffect('OnToDoUpdated', refetch);

  return isLoading
    ? (
      <div className="flex gap-2 items-center">
        <Spinner />
        <span className="text-xs">Loading...</span>
      </div>
    )
    : (
      <>
        {data.map((toDo) => (
          <Tag
            key={toDo?.toDoId}
            tagName="task"
            className={clsx('max-w-[200px] truncate', statusColorClasses[toDo?.statusDisplayName ?? 'NotStarted'])}
            tooltip={<ToDoTooltip {...toDo} />}
            tooltipProps={{ isInteractive: true }}
            onClick={() => {
              openPanel('todo', { todoId: Number(toDo.toDoId) });
            }}
          >
            {toDo.name ?? ''}
          </Tag>
        ))}
      </>
    );
}
